import React from 'react';

import { format, isValid } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { useSelector } from 'react-redux';
import { Tooltip, Whisper } from 'rsuite';
import { InfoTable } from '../../../../../../../../components';
import { optimizationPricesResultSelector } from '../../../../../../../../reducers/OtimizacaoPrecos';
import { formatNumbersToPtBr, styleFormat } from '../../../../../../../../utils/FormatNumbers';
import { useResultAnalysis } from '../../../../../hooks/useResultAnalysis';
import { COLORS } from '../../ChartVariation';
import styles from './ResultAnalysisTable.module.scss';

type StoreSeries = {
  name: string;
  storeId: string;
  data: number[];
  accumulated: number;
  accumulatedMonetary: number;
  variationDataMonetary: number[];
};

type MetricData = {
  dates: string[];
  status: string;
  haveResults: boolean;
  iraAlreadyRun: boolean;
  series: StoreSeries[];
};

type RawData = {
  profit: MetricData;
  revenue: MetricData;
};

type Variation = {
  absolute: number;
  percentage: number;
  signal: 'positive' | 'negative' | 'neutral';
};

type MetricDetails = {
  value: number;
  signal: 'positive' | 'negative' | 'neutral';
  accumulated: number;
  accumulatedMonetary: number;
  variationDataMonetary: number;
  variation?: Variation;
};

type StoreDataItem = {
  date: string;
  profit?: MetricDetails;
  revenue?: MetricDetails;
};

type StoreData = {
  store: {
    name: string;
    id: string;
  };
  data: StoreDataItem[];
};
function transformData(data: RawData): StoreData[] {
  const { profit, revenue } = data;
  const stores: Record<string, StoreData> = {};

  function computeSignal(current: number, previous: number): 'positive' | 'negative' | 'neutral' {
    if (current > previous) return 'positive';
    if (current < previous) return 'negative';
    return 'neutral';
  }

  function computeVariation(current: number, previous: number): Variation {
    const absolute = current - previous;
    const percentage = previous !== 0 ? (absolute / previous) * 100 : 0;
    const signal = computeSignal(current, previous);
    return { absolute, percentage, signal };
  }

  // Profit
  profit.series.forEach(storeSeries => {
    const name = storeSeries.name;
    const id = storeSeries.storeId;

    if (!stores[name]) {
      stores[name] = {
        store: { name, id },
        data: [],
      };
    }

    storeSeries.data.forEach((value, index) => {
      const date = profit.dates[index];
      if (!stores[name].data[index]) {
        stores[name].data[index] = { date };
      }

      const previousValue = index > 0 ? storeSeries.data[index - 1] : 0;
      const signal = computeSignal(value, previousValue);
      const variation = index > 0 ? computeVariation(value, previousValue) : undefined;

      stores[name].data[index].profit = {
        value: value > 0 ? value : value * -1,
        signal,
        accumulated: storeSeries.accumulated > 0 ? storeSeries.accumulated : storeSeries.accumulated * -1,
        variation,
        variationDataMonetary: storeSeries.variationDataMonetary[index],
        accumulatedMonetary: storeSeries.accumulatedMonetary,
      };
    });
  });

  // Revenue
  revenue.series.forEach(storeSeries => {
    const name = storeSeries.name;
    const id = storeSeries.storeId;

    if (!stores[name]) {
      stores[name] = {
        store: { name, id },
        data: [],
      };
    }

    storeSeries.data.forEach((value, index) => {
      const date = revenue.dates[index];
      if (!stores[name].data[index]) {
        stores[name].data[index] = { date };
      }

      const previousValue = index > 0 ? storeSeries.data[index - 1] : 0;
      const signal = computeSignal(value, previousValue);
      const variation = index > 0 ? computeVariation(value, previousValue) : undefined;

      stores[name].data[index].revenue = {
        value: value > 0 ? value : value * -1,
        signal,
        accumulated: storeSeries.accumulated > 0 ? storeSeries.accumulated : storeSeries.accumulated * -1,
        variation,
        variationDataMonetary: storeSeries.variationDataMonetary[index],
        accumulatedMonetary: storeSeries.accumulatedMonetary,
      };
    });
  });

  return Object.values(stores);
}

function formatValue(value: number, style: styleFormat = 'currency') {
  if (value === null || value === undefined || value === 0) {
    return 'SEM VAR.';
  }

  return formatNumbersToPtBr(value || 0, style, {
    showSuffix: false,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
}

interface ResultAnalysisTableProps {
  states: ReturnType<typeof useResultAnalysis>['states'];
}

export const ResultAnalysisTable = ({ states }: ResultAnalysisTableProps) => {
  const { isLoadingProfitChart, isLoadingRevenueChart } = states;

  const { tabs } = useSelector(optimizationPricesResultSelector);
  const { charts } = tabs.analysis;

  const { profit } = charts;
  const { Column, HeaderCell, Cell } = InfoTable;
  const DATA_KEY = 'optimization';

  const data = transformData(charts as RawData);
  const columns = profit.dates?.map(date => {
    const isValidDate = isValid(new Date(date));
    if (!isValidDate) return '';
    return format(new Date(date), 'dd/MM/yyyy', { locale: ptBR });
  });

  const isLoading = isLoadingProfitChart || isLoadingRevenueChart;

  return (
    <div className={styles.container}>
      <InfoTable data={data} dataKey={DATA_KEY} className={styles.table} loading={isLoading} minHeight={200} headerHeight={34} rowHeight={62}>
        {/* @ts-ignore */}
        <Column width={200} resizable>
          <HeaderCell>Lojas</HeaderCell>
          <Cell dataKey="store">
            {(rowData: StoreData, rowIndex: number) => (
              <Whisper trigger="hover" placement="right" speaker={<Tooltip>{rowData.store.name || rowData.store.id}</Tooltip>}>
                <div className={styles.column}>
                  <div className={styles.store}>
                    <div className={styles.dot} style={{ backgroundColor: COLORS[rowIndex] }} />
                    {rowData.store.id}
                  </div>
                  <div>{rowData.store.name}</div>
                </div>
              </Whisper>
            )}
          </Cell>
        </Column>

        {columns?.map((header, index) => {
          return (
            /* @ts-ignore */
            <Column width={180} key={header} resizable>
              <HeaderCell>{header}</HeaderCell>
              <Cell dataKey={header}>
                {(rowData: StoreData) => {
                  const {
                    value: revenueValue,
                    signal: revenueSignal,
                    variationDataMonetary: revenueVariationDataMonetary,
                  } = rowData.data[index]?.revenue ?? {};

                  const { value: profitValue, signal: profitSignal, variationDataMonetary: profitVariationDataMonetary } = rowData.data[index]?.profit ?? {};

                  return (
                    <div className={styles.column}>
                      <div className={styles.item}>
                        <Whisper trigger="hover" placement="right" speaker={<Tooltip>{index === 0 ? 'Semana anterior à otimização' : 'Faturamento'}</Tooltip>}>
                          <span className={styles.value}>{formatValue(revenueVariationDataMonetary || 0, 'currency')}</span>
                        </Whisper>

                        <div data-signal-revenue={revenueSignal || 'neutral'}>
                          {revenueSignal && revenueSignal !== 'neutral' && <span>{revenueSignal === 'positive' ? <span>+</span> : <span>-</span>}</span>}
                          <span>{formatValue(revenueValue || 0, 'percent')}</span>
                        </div>
                      </div>

                      <div className={styles.item}>
                        <Whisper trigger="hover" placement="right" speaker={<Tooltip>{index === 0 ? 'Semana anterior à otimização' : 'Lucro'}</Tooltip>}>
                          <span className={styles.value}>{formatValue(profitVariationDataMonetary || 0, 'currency')}</span>
                        </Whisper>

                        <div data-signal-profit={profitSignal || 'neutral'}>
                          {profitSignal && profitSignal !== 'neutral' && <span>{profitSignal === 'positive' ? <span>+</span> : <span>-</span>}</span>}
                          <span>{formatValue(profitValue || 0, 'percent')}</span>
                        </div>
                      </div>
                    </div>
                  );
                }}
              </Cell>
            </Column>
          );
        })}

        {/* @ts-ignore */}
        <Column width={180} resizable fixed="right">
          <HeaderCell>Acumulado</HeaderCell>
          <Cell dataKey="accumulated">
            {(rowData: StoreData) => {
              const {
                accumulated: revenueValue,
                signal: revenueSignal,
                accumulatedMonetary: revenueAccumulatedMonetary,
              } = rowData.data[rowData.data.length - 1]?.revenue ?? {};

              const {
                accumulated: profitValue,
                signal: profitSignal,
                accumulatedMonetary: profitAccumulatedMonetary,
              } = rowData.data[rowData.data.length - 1]?.profit ?? {};

              return (
                <div className={styles.column}>
                  <div className={styles.item}>
                    <Whisper trigger="hover" placement="right" speaker={<Tooltip>{'Faturamento'}</Tooltip>}>
                      <span className={styles.value}>{formatValue(revenueAccumulatedMonetary || 0, 'currency')}</span>
                    </Whisper>
                    <div data-signal-revenue={revenueSignal || 'neutral'}>
                      {revenueSignal && revenueSignal !== 'neutral' && <span>{revenueSignal === 'positive' ? <span>+</span> : <span>-</span>}</span>}
                      <span>{formatValue(revenueValue || 0, 'percent')}</span>
                    </div>
                  </div>
                  <div className={styles.item}>
                    <Whisper trigger="hover" placement="right" speaker={<Tooltip>{'Lucro'}</Tooltip>}>
                      <span className={styles.value}>{formatValue(profitAccumulatedMonetary || 0, 'currency')}</span>
                    </Whisper>
                    <div data-signal-profit={profitSignal || 'neutral'}>
                      {profitSignal && profitSignal !== 'neutral' && <span>{profitSignal === 'positive' ? <span>+</span> : <span>-</span>}</span>}
                      <span>{formatValue(profitValue || 0, 'percent')}</span>
                    </div>
                  </div>
                </div>
              );
            }}
          </Cell>
        </Column>
      </InfoTable>
    </div>
  );
};
